import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.js'
import about from './logos/about-img.webp?url'
import why1 from './logos/why-us-1.webp?url'
import why2 from './logos/why-us-2.webp?url'
import why3 from './logos/why-us-3.webp?url'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/about', priority: 0.7 }],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `About | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Learn more about ${import.meta.env.VITE_APP_DESCRIPTION}`
	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },
	]
}

export default function AboutRoute() {
	return (
		<main
			id="main"
			className="w-full pb-2 md:pb-4 lg:container lg:mx-auto lg:pb-6"
		>
			<section id="about" className="pb-8 md:pb-16 lg:pb-24">
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
					<div>
						<img
							src={about}
							alt="About TrailViewr"
							className="w-full rounded-lg shadow-lg"
						/>
					</div>
					<div>
						<h3 className="mb-4 text-2xl font-semibold">
							A platform for immersive outdoor exploration!
						</h3>
						<p className="mb-4 italic">
							We want to help everyone explore the outdoors. Whether you like
							hiking, biking, camping, climbing, or want a virtual nature
							escape, we've got you covered.
						</p>
						<p className="mb-4">
							Utilizing 360-degree imagery and video, we offer an unparalleled
							interactive experience. Want to explore a trail with friends or
							family, no matter where they are? Our shared VR adventures make it
							possible to explore a trail together.
						</p>
						<p className="mb-4">
							We also invite you to become an active participant in this
							groundbreaking community. Capture and upload your own 360-degree
							trail videos to share your unique adventures.
						</p>
						<p className="mb-4">
							Join us in reshaping the future of outdoor exploration. Let's
							discover the world, one trail at a time—become a{' '}
							{import.meta.env.VITE_APP_TITLE}
							contributor today!
						</p>
					</div>
				</div>
			</section>
			<hr className="my-8 h-px border-0 bg-muted-foreground/40" />
			<section id="why-us">
				<div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
					<Card>
						<img
							src={why1}
							alt="Our Mission"
							className="mb-4 h-48 w-full rounded-lg object-cover"
						/>
						<CardHeader>
							<CardTitle>Our Mission</CardTitle>
						</CardHeader>
						<CardContent>
							To empower all outdoor enthusiasts and explorers, regardless of
							ability, with the tools and knowledge they need for an immersive,
							informative, and inspiring experience.
						</CardContent>
					</Card>

					<Card>
						<img
							src={why2}
							alt="Our Plan"
							className="mb-4 h-48 w-full rounded-lg object-cover"
						/>
						<CardHeader>
							<CardTitle>Our Plan</CardTitle>
						</CardHeader>
						<CardContent>
							To create an interactive platform enhanced with VR/AR technology
							that brings every trail, in every season, a click or a headset
							away.
						</CardContent>
					</Card>

					<Card>
						<img
							src={why3}
							alt="Our Vision"
							className="mb-4 h-48 w-full rounded-lg object-cover"
						/>
						<CardHeader>
							<CardTitle>Our Vision</CardTitle>
						</CardHeader>
						<CardContent>
							We envision an accessible platform where every trail's natural
							beauty and challenges are open to everyone, everywhere.
						</CardContent>
					</Card>
				</div>
			</section>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
